import GraphQLClient from 'ts/GraphQLClient'
import { RepoParams } from 'ts/types'
import {
  ACTION_ALLOCATION_GAP,
  APPEND_WORKLOAD_BUSINESS_CASE,
  APPROVE_MODEL_SCOPE,
  BLOCK_MODEL_SCOPE,
  CLAIM_AI_WORKLOAD,
  GET_AI_GOVERNANCE_SCORE,
  GET_AI_VALUE_BOARD,
  GET_AI_VALUE_OPPORTUNITY,
  LIST_AI_GOVERNANCE_TASKS,
  LIST_AI_WORKLOADS,
  LIST_ALLOCATION_GAPS,
  LIST_BUSINESS_REQUESTS,
  LIST_FOUNDATION_MODELS,
  MARK_TASK_IMPLEMENTED,
  RECALL_ALLOCATION_GAP,
  RELEASE_AI_WORKLOAD,
  REVIEW_BUSINESS_REQUEST,
  SUBMIT_BUSINESS_REQUEST,
  UPDATE_AI_ESTIMATE,
  VALIDATE_MODEL_PRICING
} from './graphql'

export default class AIGovernanceRepo {
  private client: GraphQLClient

  constructor({ GRAPHQL_URL, ID_TOKEN }: RepoParams) {
    this.client = new GraphQLClient({ url: GRAPHQL_URL, idToken: ID_TOKEN })
  }

  async getAIGovernanceScore() {
    const res = await this.client.request<{ getAIGovernanceScore: any }>({
      query: GET_AI_GOVERNANCE_SCORE
    })
    return res.getAIGovernanceScore
  }

  async listFoundationModels() {
    const res = await this.client.request<{ listFoundationModels: any[] }>({
      query: LIST_FOUNDATION_MODELS
    })
    return res.listFoundationModels ?? []
  }

  async approveModelScope(input: any) {
    const res = await this.client.request<{ approveModelScope: any[] }>({
      query: APPROVE_MODEL_SCOPE,
      variables: { input }
    })
    return res.approveModelScope
  }

  async blockModelScope(input: any) {
    const res = await this.client.request<{ blockModelScope: any }>({
      query: BLOCK_MODEL_SCOPE,
      variables: { input }
    })
    return res.blockModelScope
  }

  async validateModelPricing(modelId: string) {
    const res = await this.client.request<{ validateModelPricing: any[] }>({
      query: VALIDATE_MODEL_PRICING,
      variables: { modelId }
    })
    return res.validateModelPricing
  }

  async listBusinessRequests() {
    const res = await this.client.request<{ listBusinessRequests: any[] }>({
      query: LIST_BUSINESS_REQUESTS
    })
    return res.listBusinessRequests ?? []
  }

  async reviewBusinessRequest(input: any) {
    const res = await this.client.request<{ reviewBusinessRequest: any }>({
      query: REVIEW_BUSINESS_REQUEST,
      variables: { input }
    })
    return res.reviewBusinessRequest
  }

  async listAIGovernanceTasks(status?: string) {
    const res = await this.client.request<{ listAIGovernanceTasks: any[] }>({
      query: LIST_AI_GOVERNANCE_TASKS,
      variables: status ? { status } : {}
    })
    return res.listAIGovernanceTasks ?? []
  }

  async markTaskImplemented(input: any) {
    const res = await this.client.request<{ markTaskImplemented: any }>({
      query: MARK_TASK_IMPLEMENTED,
      variables: { input }
    })
    return res.markTaskImplemented
  }

  async listAllocationGaps(status?: string) {
    const res = await this.client.request<{ listAllocationGaps: any[] }>({
      query: LIST_ALLOCATION_GAPS,
      variables: status ? { status } : {}
    })
    return res.listAllocationGaps ?? []
  }

  async actionAllocationGap(input: any) {
    const res = await this.client.request<{ actionAllocationGap: any }>({
      query: ACTION_ALLOCATION_GAP,
      variables: { input }
    })
    return res.actionAllocationGap
  }

  async recallAllocationGap(input: any) {
    const res = await this.client.request<{ recallAllocationGap: any }>({
      query: RECALL_ALLOCATION_GAP,
      variables: { input }
    })
    return res.recallAllocationGap
  }

  async getAiValueBoard(element3Map?: string, element4Map?: string) {
    const res = await this.client.request<{ getAiValueBoard: any[] }>({
      query: GET_AI_VALUE_BOARD,
      variables: { element3Map, element4Map }
    })
    return res.getAiValueBoard ?? []
  }

  async getAiValueOpportunity(element3Map?: string, element4Map?: string) {
    const res = await this.client.request<{ getAiValueOpportunity: any }>({
      query: GET_AI_VALUE_OPPORTUNITY,
      variables: { element3Map, element4Map }
    })
    return res.getAiValueOpportunity
  }

  async listAIWorkloads(element4Map?: string) {
    const res = await this.client.request<{ listAIWorkloads: any[] }>({
      query: LIST_AI_WORKLOADS,
      variables: element4Map ? { element4Map } : {}
    })
    return res.listAIWorkloads ?? []
  }

  async claimAIWorkload(workloadId: string, iamRoleArn?: string, resourceArn?: string, guardrailArn?: string) {
    const res = await this.client.request<{ claimAIWorkload: any }>({
      query: CLAIM_AI_WORKLOAD,
      variables: { workloadId, iamRoleArn, resourceArn, guardrailArn }
    })
    return res.claimAIWorkload
  }

  async releaseAIWorkload(workloadId: string) {
    const res = await this.client.request<{ releaseAIWorkload: any }>({
      query: RELEASE_AI_WORKLOAD,
      variables: { workloadId }
    })
    return res.releaseAIWorkload
  }

  async updateAIEstimate(input: {
    workloadId: string
    estimateType: string
    estimateDollar?: string
    estimateInputTokens?: string
    estimateOutputTokens?: string
    estimateCacheReadTokens?: string
  }) {
    const res = await this.client.request<{ updateAIEstimate: any }>({
      query: UPDATE_AI_ESTIMATE,
      variables: input
    })
    return res.updateAIEstimate
  }

  async submitBusinessRequest(input: any) {
    const res = await this.client.request<{ submitBusinessRequest: any }>({
      query: SUBMIT_BUSINESS_REQUEST,
      variables: { input }
    })
    return res.submitBusinessRequest
  }

  async appendWorkloadBusinessCase(input: any) {
    const res = await this.client.request<{ appendWorkloadBusinessCase: any }>({
      query: APPEND_WORKLOAD_BUSINESS_CASE,
      variables: { input }
    })
    return res.appendWorkloadBusinessCase
  }
}
